import { motion } from 'framer-motion';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { Link } from 'react-router-dom';
import { industries } from '@/data/industries';
import { IMAGE_WIDTHS, buildSrc, buildSrcSet } from '@/lib/responsiveImage';

// Map industry titles to images
const industryImages: Record<string, string> = {
  'Ärzte & Praxen': '/images/industries/aerzte',
  'Ferienwohnungen': '/images/industries/ferienwohnung',
  'Hotels & Pensionen': '/images/industries/hotel',
  'Steuerberater': '/images/industries/steuerberater',
  'Industrie': '/images/industries/industrie',
  'Immobilien': '/images/industries/immobilien',
  'E-Commerce': '/images/industries/ecommerce',
  'Vereine': '/images/industries/vereine',
};

const Industries = () => {
  // Show only first 6 on homepage
  const displayedIndustries = industries.slice(0, 6);

  const imageSizes = '(min-width: 1024px) 33vw, (min-width: 768px) 50vw, 100vw';
  return (
    <section id="branchen" className="py-24 md:py-32 relative section-dark">
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center max-w-3xl mx-auto mb-16"
        >
          <span className="text-primary font-medium text-sm uppercase tracking-wider">Branchen</span>
          <h2 className="text-3xl md:text-5xl font-display font-bold mt-4 mb-6 text-secondary-foreground">
            Für welche Branchen entwickeln wir
            <span className="gradient-text"> Webseiten?</span>
          </h2>
          <p className="text-secondary-foreground text-lg">
            Jede Branche hat ihre eigenen Anforderungen. Wir kennen die Besonderheiten 
            und entwickeln maßgeschneiderte Lösungen.
          </p>
        </motion.div>

        {/* Industries Grid */}
        <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
          {displayedIndustries.map((industry, index) => (
            <motion.div
              key={industry.title}
              initial={{ opacity: 0, scale: 0.9 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <Link
                to={`/branchen/${industry.slug}`}
                className="group block relative rounded-2xl overflow-hidden shadow-lg cursor-pointer h-[320px]"
              >
                {/* Background Image */}
                <img
                  src={buildSrc(industryImages[industry.title] || '', 800)}
                  srcSet={buildSrcSet(industryImages[industry.title] || '', IMAGE_WIDTHS.card)}
                  sizes={imageSizes}
                  alt={`${industry.title} – Branchenmotiv`}
                  loading="lazy"
                  decoding="async"
                  width={800}
                  height={544}
                  className="absolute inset-0 w-full h-full object-cover transition-transform duration-500 group-hover:scale-110"
                />
                
                {/* Dark Overlay */}
                <div className="absolute inset-0 photo-card-overlay" />

                {/* Content */}
                <div className="relative z-10 h-full flex flex-col justify-end p-6">
                  {/* Icon */}
                  <div className={`w-14 h-14 rounded-xl bg-gradient-to-br ${industry.color} backdrop-blur-sm flex items-center justify-center mb-4 border border-photo-border`}>
                    <industry.icon className={`w-7 h-7 ${industry.iconColor}`} aria-hidden="true" />
                  </div>

                  {/* Title & Description */}
                  <h3 className="text-xl font-display font-semibold mb-2 flex items-center gap-2 text-photo-foreground">
                    {industry.title}
                    <ArrowUpRight className="w-5 h-5 opacity-0 group-hover:opacity-100 transition-opacity text-primary" aria-hidden="true" />
                  </h3>
                  <p className="text-photo-foreground text-sm leading-relaxed">{industry.description}</p>
                </div>
              </Link>
            </motion.div>
          ))}
        </div>

        {/* View All Button */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          className="text-center mt-12"
        >
          <Link
            to="/branchen"
            className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-primary text-primary-foreground font-semibold hover:bg-primary/90 transition-colors shadow-lg"
          >
            Alle Branchen ansehen
            <ArrowRight className="w-5 h-5" aria-hidden="true" />
          </Link>
        </motion.div>
      </div>
    </section>
  );
};

export default Industries;
